<template>
  <div class="commission-report">
    <div class="report-header">
      <div>
        <span class="section-kicker">Reportes</span>
        <h2>Liquidacion de comisiones</h2>
        <p>Rol de comisiones, simulador, aprobacion multinivel y estado de cuenta por vendedor.</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-outline-primary btn-sm" @click="modalSimuladorVisible = true">
          <i class="bi bi-sliders me-1"></i>
          Simular
        </button>
        <button v-if="esAdministrador" class="btn btn-warning btn-sm" @click="abrirModalCalcular">
          <i class="bi bi-calculator me-1"></i>
          Calcular mes
        </button>
      </div>
    </div>

    <div class="filter-panel">
      <div class="row g-2 align-items-end">
        <div class="col-sm-4 col-lg-2">
          <label class="form-label form-label-sm">Anio</label>
          <select class="form-select form-select-sm" v-model="filtros.anio">
            <option value="">Todos</option>
            <option v-for="a in aniosDisponibles" :key="a" :value="a">{{ a }}</option>
          </select>
        </div>
        <div class="col-sm-4 col-lg-2">
          <label class="form-label form-label-sm">Mes</label>
          <select class="form-select form-select-sm" v-model="filtros.mes">
            <option value="">Todos</option>
            <option v-for="m in meses" :key="m.valor" :value="m.valor">{{ m.nombre }}</option>
          </select>
        </div>
        <div class="col-sm-4 col-lg-2">
          <label class="form-label form-label-sm">Estado</label>
          <select class="form-select form-select-sm" v-model="filtros.estado">
            <option value="">Todos</option>
            <option value="borrador">Borrador</option>
            <option value="aprobado">Aprobado</option>
            <option value="validado">Validado</option>
            <option value="pagado">Pagado</option>
          </select>
        </div>
        <div class="col-lg-6 d-flex gap-2 justify-content-lg-end flex-wrap">
          <button class="btn btn-primary btn-sm" @click="cargar" :disabled="cargando">
            <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-search me-1"></i>
            Buscar
          </button>
          <button class="btn btn-outline-secondary btn-sm" @click="limpiarFiltros">
            <i class="bi bi-eraser me-1"></i>
            Limpiar
          </button>
        </div>
      </div>
    </div>

    <div v-if="errorMensaje" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="bi bi-x-circle-fill"></i>
      <span>{{ errorMensaje }}</span>
      <button type="button" class="btn-close ms-auto" @click="errorMensaje = ''"></button>
    </div>
    <div v-if="exitoMensaje" class="alert alert-success d-flex align-items-center gap-2">
      <i class="bi bi-check-circle-fill"></i>
      <span>{{ exitoMensaje }}</span>
      <button type="button" class="btn-close ms-auto" @click="exitoMensaje = ''"></button>
    </div>

    <div class="row g-3 mb-3">
      <div class="col-md-3 col-6">
        <div class="metric-card">
          <span>Base comisionable</span>
          <strong>{{ formatearMoneda(totalBase) }}</strong>
          <small>{{ datos.length }} vendedor(es)</small>
        </div>
      </div>
      <div class="col-md-3 col-6">
        <div class="metric-card">
          <span>Comision calculada</span>
          <strong>{{ formatearMoneda(totalComision) }}</strong>
          <small>{{ porcentajePromedio }}% promedio</small>
        </div>
      </div>
      <div class="col-md-3 col-6">
        <div class="metric-card">
          <span>Devoluciones</span>
          <strong>{{ formatearMoneda(totalDevoluciones) }}</strong>
          <small>Notas de credito del mes</small>
        </div>
      </div>
      <div class="col-md-3 col-6">
        <div class="metric-card danger">
          <span>Bloqueos cartera</span>
          <strong>{{ totalBloqueados }}</strong>
          <small>Comisiones retenidas</small>
        </div>
      </div>
    </div>

    <div class="approval-panel">
      <div class="approval-step done">
        <i class="bi bi-calculator"></i>
        <div>
          <strong>Borrador</strong>
          <small>{{ conteoEstado('borrador') }} por revisar</small>
        </div>
      </div>
      <div class="approval-step active">
        <i class="bi bi-person-check"></i>
        <div>
          <strong>Contabilidad / Gerencia</strong>
          <small>{{ conteoEstado('aprobado') }} aprobado(s)</small>
        </div>
      </div>
      <div class="approval-step">
        <i class="bi bi-bank"></i>
        <div>
          <strong>Tesoreria</strong>
          <small>{{ conteoEstado('validado') }} validado(s)</small>
        </div>
      </div>
      <div class="approval-step">
        <i class="bi bi-lock"></i>
        <div>
          <strong>Periodo cerrado</strong>
          <small>{{ conteoEstado('pagado') }} pagado(s)</small>
        </div>
      </div>
    </div>

    <div v-if="cargando" class="empty-state">
      <span class="spinner-border text-primary"></span>
      <p>Cargando liquidacion...</p>
    </div>

    <div v-else-if="datos.length === 0" class="empty-state">
      <i class="bi bi-cash-coin"></i>
      <p>Sin registros de comisiones para los filtros seleccionados.</p>
      <button v-if="esAdministrador" class="btn btn-warning btn-sm" @click="abrirModalCalcular">
        Calcular periodo
      </button>
    </div>

    <div v-else class="report-panel">
      <div class="panel-title">
        <div>
          <span class="section-kicker">Rol de comisiones</span>
          <h5>Detalle por vendedor</h5>
        </div>
        <span class="muted">{{ datos.length }} registro(s)</span>
      </div>

      <div class="table-responsive">
        <table class="commission-table">
          <thead>
            <tr>
              <th>Vendedor</th>
              <th>Perfil</th>
              <th class="text-center">Periodo</th>
              <th class="text-end">Base</th>
              <th class="text-end">Devoluciones</th>
              <th class="text-end">%</th>
              <th class="text-end">Comision</th>
              <th class="text-end">Sueldo</th>
              <th class="text-end">Total a pagar</th>
              <th class="text-center">Estado</th>
              <th class="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="fila in datos" :key="fila.id" :class="{ blocked: toBool(fila.bloqueado_cartera) }">
              <td>
                <strong>{{ fila.nombre_vendedor }}</strong>
                <small v-if="fila.motivo_ajuste" class="d-block muted">{{ fila.motivo_ajuste }}</small>
              </td>
              <td>
                <div class="profile-stack">
                  <span>{{ etiquetaNaturaleza(fila.naturaleza_vendedor) }}</span>
                  <span>{{ etiquetaMetodo(fila.metodo_calculo) }}</span>
                </div>
              </td>
              <td class="text-center">{{ nombreMes(fila.mes) }} {{ fila.anio }}</td>
              <td class="text-end">{{ formatearMoneda(baseFila(fila)) }}</td>
              <td class="text-end muted">{{ formatearMoneda(fila.devoluciones_aplicadas) }}</td>
              <td class="text-end">{{ Number(fila.porcentaje_aplicado || 0).toFixed(2) }}%</td>
              <td class="text-end text-primary fw-semibold">{{ formatearMoneda(fila.comision_calculada) }}</td>
              <td class="text-end muted">{{ formatearMoneda(fila.sueldo_calculado) }}</td>
              <td class="text-end text-success fw-bold">{{ formatearMoneda(fila.total_a_pagar) }}</td>
              <td class="text-center">
                <span :class="['status-pill', claseEstado(fila.estado)]">
                  {{ etiquetaEstado(fila.estado) }}
                </span>
              </td>
              <td class="text-center">
                <div class="action-group">
                  <button class="btn btn-outline-info btn-sm icon-btn" @click="verDetalle(fila)" title="Ver rol factura por factura">
                    <i class="bi bi-list-ul"></i>
                  </button>
                  <template v-if="esAdministrador">
                    <button v-if="fila.estado === 'borrador'" class="btn btn-outline-primary btn-sm icon-btn"
                            @click="cambiarEstadoFila(fila, 'aprobado')" title="Aprobar por Contabilidad/Gerencia">
                      <i class="bi bi-person-check"></i>
                    </button>
                    <button v-if="fila.estado === 'aprobado'" class="btn btn-outline-success btn-sm icon-btn"
                            @click="abrirModalValidar(fila)" title="Validar para Tesoreria">
                      <i class="bi bi-check2-circle"></i>
                    </button>
                    <button v-if="fila.estado === 'validado'" class="btn btn-outline-dark btn-sm icon-btn"
                            @click="cambiarEstadoFila(fila, 'pagado')" title="Marcar como pagado y cerrar">
                      <i class="bi bi-lock"></i>
                    </button>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="3">TOTALES</td>
              <td class="text-end">{{ formatearMoneda(totalBase) }}</td>
              <td class="text-end">{{ formatearMoneda(totalDevoluciones) }}</td>
              <td></td>
              <td class="text-end">{{ formatearMoneda(totalComision) }}</td>
              <td class="text-end">{{ formatearMoneda(totalSueldo) }}</td>
              <td class="text-end">{{ formatearMoneda(totalAPagar) }}</td>
              <td colspan="2"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <div v-if="modalCalcularVisible" class="modal-overlay" @click.self="modalCalcularVisible = false">
      <div class="modal-contenedor modal-smx">
        <div class="modal-cabecera">
          <div>
            <span class="section-kicker">Proceso mensual</span>
            <h5>Calcular comisiones</h5>
          </div>
          <button class="btn-close" @click="modalCalcularVisible = false"></button>
        </div>
        <div class="modal-cuerpo">
          <p class="muted">
            Procesa vendedores activos, descuenta notas de credito del mes y respeta
            bloqueos por cartera vencida si la regla lo tiene configurado.
          </p>
          <div class="row g-3">
            <div class="col-6">
              <label class="form-label">Anio *</label>
              <input type="number" class="form-control" v-model.number="calcular.anio" min="2020" max="2100">
            </div>
            <div class="col-6">
              <label class="form-label">Mes *</label>
              <select class="form-select" v-model.number="calcular.mes">
                <option v-for="m in meses" :key="m.valor" :value="m.valor">{{ m.nombre }}</option>
              </select>
            </div>
          </div>
          <div class="warning-box">
            Las comisiones validadas o pagadas no se recalculan. Un periodo pagado queda bloqueado.
          </div>
        </div>
        <div class="modal-pie">
          <button class="btn btn-secondary" @click="modalCalcularVisible = false" :disabled="calculando">Cancelar</button>
          <button class="btn btn-primary" @click="ejecutarCalculo" :disabled="calculando">
            <span v-if="calculando" class="spinner-border spinner-border-sm me-1"></span>
            Calcular
          </button>
        </div>
      </div>
    </div>

    <div v-if="modalSimuladorVisible" class="modal-overlay" @click.self="modalSimuladorVisible = false">
      <div class="modal-contenedor">
        <div class="modal-cabecera">
          <div>
            <span class="section-kicker">Proyeccion</span>
            <h5>Simulador de comisiones</h5>
          </div>
          <button class="btn-close" @click="modalSimuladorVisible = false"></button>
        </div>
        <div class="modal-cuerpo">
          <div class="row g-3">
            <div class="col-lg-7">
              <div class="sim-grid">
                <div>
                  <label class="form-label">Base proyectada</label>
                  <div class="input-group">
                    <span class="input-group-text">$</span>
                    <input type="number" class="form-control" v-model.number="simulador.base" min="0" step="0.01">
                  </div>
                </div>
                <div>
                  <label class="form-label">Meta</label>
                  <div class="input-group">
                    <span class="input-group-text">$</span>
                    <input type="number" class="form-control" v-model.number="simulador.meta" min="0" step="0.01">
                  </div>
                </div>
                <div>
                  <label class="form-label">Devoluciones</label>
                  <div class="input-group">
                    <span class="input-group-text">$</span>
                    <input type="number" class="form-control" v-model.number="simulador.devoluciones" min="0" step="0.01">
                  </div>
                </div>
                <div>
                  <label class="form-label">Descuento otorgado</label>
                  <div class="input-group">
                    <input type="number" class="form-control" v-model.number="simulador.descuento_pct" min="0" max="100" step="0.1">
                    <span class="input-group-text">%</span>
                  </div>
                </div>
                <div>
                  <label class="form-label">Sueldo / fijo</label>
                  <div class="input-group">
                    <span class="input-group-text">$</span>
                    <input type="number" class="form-control" v-model.number="simulador.sueldo" min="0" step="0.01">
                  </div>
                </div>
                <div>
                  <label class="form-label">Metodo</label>
                  <select class="form-select" v-model="simulador.metodo">
                    <option value="cumplimiento_meta">Cumplimiento de meta</option>
                    <option value="progresivo_monto">Tiers por monto</option>
                  </select>
                </div>
              </div>
            </div>
            <div class="col-lg-5">
              <div class="sim-result">
                <span>Resultado estimado</span>
                <strong>{{ formatearMoneda(simulacion.total) }}</strong>
                <div class="sim-lines">
                  <div><span>Base final</span><b>{{ formatearMoneda(simulacion.baseFinal) }}</b></div>
                  <div><span>Cumplimiento</span><b>{{ simulacion.cumplimiento.toFixed(1) }}%</b></div>
                  <div><span>Porcentaje</span><b>{{ simulacion.porcentaje.toFixed(2) }}%</b></div>
                  <div><span>Comision</span><b>{{ formatearMoneda(simulacion.comision) }}</b></div>
                  <div><span>Penalizacion</span><b>{{ formatearMoneda(simulacion.penalizacion) }}</b></div>
                </div>
                <div :class="['sim-alert', simulacion.penalizado ? 'is-risk' : 'is-ok']">
                  {{ simulacion.mensaje }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-pie">
          <button class="btn btn-secondary" @click="modalSimuladorVisible = false">Cerrar</button>
        </div>
      </div>
    </div>

    <div v-if="modalValidarVisible" class="modal-overlay" @click.self="modalValidarVisible = false">
      <div class="modal-contenedor modal-smx">
        <div class="modal-cabecera">
          <div>
            <span class="section-kicker">Tesoreria</span>
            <h5>Validar y registrar pago</h5>
          </div>
          <button class="btn-close" @click="modalValidarVisible = false"></button>
        </div>
        <div class="modal-cuerpo">
          <div v-if="filaValidar" class="summary-box">
            <strong>{{ filaValidar.nombre_vendedor }}</strong>
            <span>{{ nombreMes(filaValidar.mes) }} {{ filaValidar.anio }}</span>
            <b>{{ formatearMoneda(filaValidar.total_a_pagar) }}</b>
          </div>

          <label class="form-label">Fuente de pago *</label>
          <div class="segmented mb-3">
            <button :class="{ active: validar.fuente_pago === 'caja' }" @click="validar.fuente_pago = 'caja'">
              Caja
            </button>
            <button :class="{ active: validar.fuente_pago === 'banco' }" @click="validar.fuente_pago = 'banco'">
              Banco
            </button>
          </div>

          <div v-if="validar.fuente_pago === 'caja'" class="mb-3">
            <label class="form-label">Caja *</label>
            <select class="form-select" v-model="validar.caja_id">
              <option value="">Selecciona una caja...</option>
              <option v-for="c in cuentas.cajas" :key="c.id" :value="c.id">{{ c.nombre }}</option>
            </select>
          </div>

          <div v-if="validar.fuente_pago === 'banco'" class="mb-3">
            <label class="form-label">Banco *</label>
            <select class="form-select" v-model="validar.banco_id">
              <option value="">Selecciona un banco...</option>
              <option v-for="b in cuentas.bancos" :key="b.id" :value="b.id">{{ b.nombre }}</option>
            </select>
          </div>

          <label class="form-label">Observacion</label>
          <textarea class="form-control" rows="2" v-model="validar.observacion" placeholder="Opcional"></textarea>
        </div>
        <div class="modal-pie">
          <button class="btn btn-secondary" @click="modalValidarVisible = false" :disabled="validando">Cancelar</button>
          <button class="btn btn-success" @click="ejecutarValidacion" :disabled="validando">
            <span v-if="validando" class="spinner-border spinner-border-sm me-1"></span>
            Validar pago
          </button>
        </div>
      </div>
    </div>

    <div v-if="modalDetalleVisible" class="modal-overlay" @click.self="modalDetalleVisible = false">
      <div class="modal-contenedor">
        <div class="modal-cabecera">
          <div>
            <span class="section-kicker">Rol factura por factura</span>
            <h5>{{ filaDetalle?.nombre_vendedor }} - {{ nombreMes(filaDetalle?.mes) }} {{ filaDetalle?.anio }}</h5>
          </div>
          <button class="btn-close" @click="modalDetalleVisible = false"></button>
        </div>
        <div class="modal-cuerpo">
          <div v-if="cargandoDetalle" class="empty-state compact">
            <span class="spinner-border text-primary"></span>
          </div>
          <div v-else-if="detalleFacturas.length === 0" class="empty-state compact">
            Sin facturas para este periodo.
          </div>
          <div v-else class="table-responsive">
            <table class="commission-table detail">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Factura</th>
                  <th>Cliente</th>
                  <th class="text-end">Venta neta</th>
                  <th class="text-end">Descuento</th>
                  <th class="text-end">Margen</th>
                  <th class="text-end">IVA</th>
                  <th class="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="factura in detalleFacturas" :key="factura.id">
                  <td>{{ factura.fecha_emision }}</td>
                  <td class="mono">{{ factura.numero_factura }}</td>
                  <td>{{ factura.cliente }}</td>
                  <td class="text-end">{{ formatearMoneda(factura.subtotal) }}</td>
                  <td class="text-end muted">{{ formatearMoneda(factura.descuento_total || factura.descuento) }}</td>
                  <td class="text-end">{{ formatearMoneda(factura.margen_estimado) }}</td>
                  <td class="text-end muted">{{ formatearMoneda(factura.iva) }}</td>
                  <td class="text-end fw-semibold">{{ formatearMoneda(factura.total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="modal-pie">
          <button class="btn btn-secondary" @click="modalDetalleVisible = false">Cerrar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { comisionesService } from '@/services/comisionesService'

export default {
  name: 'ClientReporteComisiones',

  data () {
    const hoy = new Date()
    return {
      cargando: false,
      errorMensaje: '',
      exitoMensaje: '',
      filtros: {
        anio: hoy.getFullYear(),
        mes: hoy.getMonth() + 1,
        estado: ''
      },
      datos: [],
      cuentas: { cajas: [], bancos: [] },
      modalCalcularVisible: false,
      calculando: false,
      calcular: {
        anio: hoy.getFullYear(),
        mes: hoy.getMonth() + 1
      },
      modalSimuladorVisible: false,
      simulador: {
        base: 12000,
        meta: 10000,
        devoluciones: 0,
        descuento_pct: 0,
        sueldo: 450,
        metodo: 'cumplimiento_meta'
      },
      modalValidarVisible: false,
      validando: false,
      filaValidar: null,
      validar: {
        fuente_pago: 'caja',
        caja_id: '',
        banco_id: '',
        observacion: ''
      },
      modalDetalleVisible: false,
      cargandoDetalle: false,
      filaDetalle: null,
      detalleFacturas: [],
      meses: [
        { valor: 1, nombre: 'Enero' },
        { valor: 2, nombre: 'Febrero' },
        { valor: 3, nombre: 'Marzo' },
        { valor: 4, nombre: 'Abril' },
        { valor: 5, nombre: 'Mayo' },
        { valor: 6, nombre: 'Junio' },
        { valor: 7, nombre: 'Julio' },
        { valor: 8, nombre: 'Agosto' },
        { valor: 9, nombre: 'Septiembre' },
        { valor: 10, nombre: 'Octubre' },
        { valor: 11, nombre: 'Noviembre' },
        { valor: 12, nombre: 'Diciembre' }
      ]
    }
  },

  computed: {
    ...mapGetters(['esAdministrador', 'currentUser']),

    aniosDisponibles () {
      const anioActual = new Date().getFullYear()
      return Array.from({ length: 6 }, (_, i) => anioActual - i)
    },
    totalFacturado () {
      return this.datos.reduce((s, f) => s + Number(f.total_facturado || 0), 0)
    },
    totalBase () {
      return this.datos.reduce((s, f) => s + this.baseFila(f), 0)
    },
    totalSueldo () {
      return this.datos.reduce((s, f) => s + Number(f.sueldo_calculado || 0), 0)
    },
    totalComision () {
      return this.datos.reduce((s, f) => s + Number(f.comision_calculada || 0), 0)
    },
    totalBono () {
      return this.datos.reduce((s, f) => s + Number(f.bono_venta || 0), 0)
    },
    totalAPagar () {
      return this.datos.reduce((s, f) => s + Number(f.total_a_pagar || 0), 0)
    },
    totalDevoluciones () {
      return this.datos.reduce((s, f) => s + Number(f.devoluciones_aplicadas || 0), 0)
    },
    totalBloqueados () {
      return this.datos.filter(f => this.toBool(f.bloqueado_cartera)).length
    },
    porcentajePromedio () {
      return this.totalBase > 0 ? ((this.totalComision / this.totalBase) * 100).toFixed(2) : '0.00'
    },
    simulacion () {
      const baseFinal = Math.max(Number(this.simulador.base || 0) - Number(this.simulador.devoluciones || 0), 0)
      const cumplimiento = Number(this.simulador.meta || 0) > 0 ? (baseFinal / Number(this.simulador.meta)) * 100 : 0
      let porcentaje = 0

      if (this.simulador.metodo === 'cumplimiento_meta') {
        if (cumplimiento >= 100) porcentaje = 5
        else if (cumplimiento >= 71) porcentaje = 3.5
        else if (cumplimiento >= 51) porcentaje = 2
      } else {
        if (baseFinal > 15000) porcentaje = 5
        else if (baseFinal > 5000) porcentaje = 3.5
        else if (baseFinal > 0) porcentaje = 2
      }

      const bruto = baseFinal * (porcentaje / 100)
      const penalizado = Number(this.simulador.descuento_pct || 0) > 15 && porcentaje > 2
      const porcentajeFinal = penalizado ? 2 : porcentaje
      const comision = baseFinal * (porcentajeFinal / 100)
      const penalizacion = Math.max(bruto - comision, 0)

      return {
        baseFinal,
        cumplimiento,
        porcentaje: porcentajeFinal,
        comision,
        penalizacion,
        penalizado,
        total: Number(this.simulador.sueldo || 0) + comision,
        mensaje: penalizado
          ? 'Descuento mayor al 15%: la comision cae al rango minimo.'
          : 'Sin penalizaciones aplicadas en la simulacion.'
      }
    }
  },

  mounted () {
    this.cargar()
    if (this.esAdministrador) this.cargarCuentas()
  },

  methods: {
    async cargar () {
      this.cargando = true
      this.errorMensaje = ''
      try {
        if (this.esAdministrador) {
          const resp = await comisionesService.getReportePeriodo(this.filtros)
          this.datos = resp.data || []
        } else {
          // Un vendedor solo puede ver su propio historico (el backend ya lo
          // exige); ese endpoint no acepta filtros de periodo, se filtran aqui.
          const miId = this.currentUser?.id
          const resp = miId ? await comisionesService.getHistorialVendedor(miId) : { data: [] }
          this.datos = (resp.data || [])
            .filter(f => (!this.filtros.anio  || Number(f.anio) === Number(this.filtros.anio)))
            .filter(f => (!this.filtros.mes   || Number(f.mes)  === Number(this.filtros.mes)))
            .filter(f => (!this.filtros.estado || f.estado === this.filtros.estado))
            .map(f => ({
              usuario_id: miId,
              nombre_vendedor: this.currentUser?.nombres_apellidos || this.currentUser?.nombre || '',
              naturaleza_vendedor: '',
              metodo_calculo: '',
              porcentaje_aplicado: 0,
              devoluciones_aplicadas: 0,
              bloqueado_cartera: 0,
              motivo_ajuste: null,
              ...f,
            }))
        }
      } catch (e) {
        this.errorMensaje = e?.response?.data?.error || 'Error al cargar el reporte.'
      } finally {
        this.cargando = false
      }
    },
    async cargarCuentas () {
      try {
        const resp = await comisionesService.getCuentas()
        this.cuentas = resp.data || { cajas: [], bancos: [] }
      } catch {
        this.cuentas = { cajas: [], bancos: [] }
      }
    },
    limpiarFiltros () {
      const hoy = new Date()
      this.filtros = { anio: hoy.getFullYear(), mes: hoy.getMonth() + 1, estado: '' }
      this.cargar()
    },
    abrirModalCalcular () {
      this.calcular = { anio: this.filtros.anio || new Date().getFullYear(), mes: this.filtros.mes || new Date().getMonth() + 1 }
      this.modalCalcularVisible = true
    },
    async ejecutarCalculo () {
      this.calculando = true
      this.errorMensaje = ''
      try {
        const resp = await comisionesService.calcularMes({
          anio: this.calcular.anio,
          mes: this.calcular.mes
        })
        const n = (resp.resultados || []).length
        this.exitoMensaje = `Comisiones calculadas: ${n} vendedor(es) procesado(s).`
        this.modalCalcularVisible = false
        await this.cargar()
      } catch (e) {
        this.errorMensaje = e?.response?.data?.error || 'Error al calcular comisiones.'
      } finally {
        this.calculando = false
      }
    },
    abrirModalValidar (fila) {
      this.filaValidar = fila
      this.validar = { fuente_pago: 'caja', caja_id: '', banco_id: '', observacion: '' }
      this.modalValidarVisible = true
    },
    async ejecutarValidacion () {
      const datos = {
        comision_id: this.filaValidar.id,
        fuente_pago: this.validar.fuente_pago,
        caja_id: this.validar.fuente_pago === 'caja' ? this.validar.caja_id : null,
        banco_id: this.validar.fuente_pago === 'banco' ? this.validar.banco_id : null,
        observacion: this.validar.observacion || null
      }

      this.validando = true
      this.errorMensaje = ''
      try {
        await comisionesService.validarComision(datos)
        this.exitoMensaje = 'Comision validada para pago.'
        this.modalValidarVisible = false
        await this.cargar()
      } catch (e) {
        this.errorMensaje = e?.response?.data?.error || 'Error al validar la comision.'
      } finally {
        this.validando = false
      }
    },
    async cambiarEstadoFila (fila, estado) {
      try {
        await comisionesService.cambiarEstado(fila.id, estado)
        this.exitoMensaje = estado === 'pagado'
          ? 'Comision pagada. El periodo queda cerrado para este vendedor.'
          : `Estado actualizado a ${this.etiquetaEstado(estado)}.`
        await this.cargar()
      } catch (e) {
        this.errorMensaje = e?.response?.data?.error || 'Error al cambiar el estado.'
      }
    },
    async verDetalle (fila) {
      this.filaDetalle = fila
      this.detalleFacturas = []
      this.cargandoDetalle = true
      this.modalDetalleVisible = true
      try {
        const resp = await comisionesService.getDetalleComision(fila.usuario_id, fila.anio, fila.mes)
        this.detalleFacturas = resp.data || []
      } catch (e) {
        this.errorMensaje = e?.response?.data?.error || 'Error al cargar el detalle.'
        this.modalDetalleVisible = false
      } finally {
        this.cargandoDetalle = false
      }
    },
    conteoEstado (estado) {
      return this.datos.filter(f => f.estado === estado).length
    },
    baseFila (fila) {
      return Number(fila.base_comisionable || fila.total_facturado || 0)
    },
    nombreMes (num) {
      const mes = this.meses.find(x => Number(x.valor) === Number(num))
      return mes ? mes.nombre : (num || '')
    },
    etiquetaEstado (estado) {
      const etiquetas = {
        borrador: 'Borrador',
        aprobado: 'Aprobado',
        validado: 'Validado',
        pagado: 'Pagado'
      }
      return etiquetas[estado] || estado
    },
    claseEstado (estado) {
      const clases = {
        borrador: 'draft',
        aprobado: 'approved',
        validado: 'validated',
        pagado: 'paid'
      }
      return clases[estado] || 'draft'
    },
    etiquetaNaturaleza (valor) {
      return valor === 'externo' ? 'Externo' : 'Interno'
    },
    etiquetaMetodo (valor) {
      const etiquetas = {
        cumplimiento_meta: 'Meta',
        progresivo_monto: 'Tiers',
        mixto: 'Mixto'
      }
      return etiquetas[valor] || 'Tiers'
    },
    toBool (valor) {
      return valor === true || valor === 1 || valor === '1'
    },
    formatearMoneda (valor) {
      return '$ ' + Number(valor || 0).toFixed(2)
    }
  }
}
</script>

<style scoped>
.commission-report {
  padding: 1.25rem;
  color: var(--text-primary, #1f2937);
}

.report-header,
.header-actions,
.panel-title,
.modal-cabecera,
.modal-pie,
.action-group {
  display: flex;
  align-items: center;
  gap: .75rem;
}

.report-header,
.panel-title,
.modal-cabecera,
.modal-pie {
  justify-content: space-between;
}

.report-header {
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.report-header h2 {
  margin: 0;
  font-weight: 750;
}

.report-header p,
.muted {
  color: var(--text-muted, #6b7280);
}

.section-kicker {
  display: inline-block;
  margin-bottom: .25rem;
  color: var(--primary-color, #1173d4);
  font-size: .72rem;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.filter-panel,
.metric-card,
.approval-panel,
.report-panel,
.modal-contenedor,
.summary-box,
.sim-result {
  background: var(--card-bg, #fff);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, .05);
}

.filter-panel {
  padding: 1rem;
  margin-bottom: 1rem;
}

.metric-card {
  min-height: 118px;
  padding: 1rem;
}

.metric-card span,
.sim-result > span {
  display: block;
  color: var(--text-muted, #6b7280);
  font-size: .76rem;
  font-weight: 800;
}

.metric-card strong {
  display: block;
  margin-top: .3rem;
  font-size: 1.55rem;
  color: var(--primary-color, #1173d4);
}

.metric-card.danger strong {
  color: #b42318;
}

.metric-card small {
  color: var(--text-muted, #6b7280);
}

.approval-panel {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: .75rem;
  padding: .85rem;
  margin-bottom: 1rem;
}

.approval-step {
  display: flex;
  gap: .75rem;
  align-items: center;
  padding: .85rem;
  border-radius: 8px;
  background: var(--table-header-bg, #f8fafc);
}

.approval-step i {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  color: var(--primary-color, #1173d4);
  background: rgba(17, 115, 212, .1);
}

.approval-step.done i {
  color: #146c43;
  background: rgba(25, 135, 84, .12);
}

.approval-step.active i {
  color: #92400e;
  background: rgba(245, 158, 11, .14);
}

.approval-step strong,
.approval-step small {
  display: block;
}

.approval-step small {
  color: var(--text-muted, #6b7280);
}

.report-panel {
  overflow: hidden;
}

.panel-title {
  padding: 1rem;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
}

.panel-title h5 {
  margin: 0;
  font-weight: 750;
}

.commission-table {
  width: 100%;
  border-collapse: collapse;
  font-size: .86rem;
}

.commission-table th,
.commission-table td {
  padding: .72rem .85rem;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
  vertical-align: middle;
}

.commission-table thead th,
.commission-table tfoot td {
  background: var(--table-header-bg, #f8fafc);
  font-weight: 800;
  white-space: nowrap;
}

.commission-table tbody tr:hover td {
  background: rgba(17, 115, 212, .03);
}

.commission-table tbody tr.blocked td {
  background: rgba(180, 35, 24, .04);
}

.profile-stack {
  display: flex;
  gap: .35rem;
  flex-wrap: wrap;
}

.profile-stack span,
.status-pill {
  border-radius: 999px;
  padding: .25rem .55rem;
  font-size: .72rem;
  font-weight: 800;
}

.profile-stack span {
  color: var(--primary-color, #1173d4);
  background: rgba(17, 115, 212, .09);
}

.status-pill.draft {
  color: #475569;
  background: rgba(100, 116, 139, .14);
}

.status-pill.approved {
  color: #075985;
  background: rgba(14, 165, 233, .14);
}

.status-pill.validated {
  color: #92400e;
  background: rgba(245, 158, 11, .16);
}

.status-pill.paid {
  color: #146c43;
  background: rgba(25, 135, 84, .14);
}

.action-group {
  justify-content: center;
}

.icon-btn {
  width: 32px;
  height: 30px;
  padding: 0;
}

.empty-state {
  padding: 3rem 1rem;
  text-align: center;
  color: var(--text-muted, #6b7280);
}

.empty-state.compact {
  padding: 1.5rem 1rem;
}

.empty-state i {
  display: block;
  margin-bottom: .75rem;
  font-size: 2.25rem;
  color: var(--primary-color, #1173d4);
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, .55);
}

.modal-contenedor {
  width: min(980px, 100%);
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 22px 70px rgba(0, 0, 0, .22);
}

.modal-smx {
  width: min(520px, 100%);
}

.modal-cabecera,
.modal-pie {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
}

.modal-pie {
  justify-content: flex-end;
  border-top: 1px solid var(--border-color, #e2e8f0);
  border-bottom: 0;
}

.modal-cuerpo {
  padding: 1.25rem;
  overflow-y: auto;
}

.warning-box,
.sim-alert {
  margin-top: 1rem;
  padding: .85rem;
  border-radius: 8px;
  color: #92400e;
  background: rgba(245, 158, 11, .14);
  border: 1px solid rgba(245, 158, 11, .28);
  font-weight: 700;
}

.summary-box {
  padding: .85rem;
  margin-bottom: 1rem;
}

.summary-box strong,
.summary-box span,
.summary-box b {
  display: block;
}

.summary-box b {
  color: #146c43;
}

.segmented {
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: .25rem;
  background: var(--table-header-bg, #f1f5f9);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
}

.segmented button {
  border: 0;
  border-radius: 6px;
  padding: .55rem .75rem;
  background: transparent;
  color: var(--text-muted, #6b7280);
  font-weight: 800;
}

.segmented button.active {
  background: #fff;
  color: var(--primary-color, #1173d4);
  box-shadow: 0 4px 12px rgba(15, 23, 42, .08);
}

.sim-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.sim-result {
  height: 100%;
  padding: 1rem;
}

.sim-result strong {
  display: block;
  margin: .35rem 0 1rem;
  color: var(--primary-color, #1173d4);
  font-size: 2rem;
}

.sim-lines div {
  display: flex;
  justify-content: space-between;
  padding: .45rem 0;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
}

.sim-alert.is-ok {
  color: #146c43;
  background: rgba(25, 135, 84, .1);
  border-color: rgba(25, 135, 84, .2);
}

.sim-alert.is-risk {
  color: #92400e;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

@media (max-width: 992px) {
  .approval-panel,
  .sim-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .commission-report {
    padding: .85rem;
  }

  .approval-panel,
  .sim-grid {
    grid-template-columns: 1fr;
  }
}
</style>

